import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNotificationAnalytics, TemplateAnalytics } from '@/hooks/useNotificationAnalytics';
import { Mail, MousePointer, TrendingUp, Users, AlertCircle, BarChart3, Eye } from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

interface NotificationAnalyticsDashboardProps {
  templateId?: string;
}

const NotificationAnalyticsDashboard: React.FC<NotificationAnalyticsDashboardProps> = ({
  templateId
}) => {
  const { analytics, isLoading, getAnalyticsStats, getTemplateAnalytics, fetchAnalytics } = useNotificationAnalytics();
  const [templateAnalytics, setTemplateAnalytics] = useState<TemplateAnalytics[]>([]);
  const [dateRange, setDateRange] = useState('7'); // days
  const [selectedMetric, setSelectedMetric] = useState<'opens' | 'clicks' | 'bounces'>('opens');

  useEffect(() => {
    const loadTemplateAnalytics = async () => {
      const data = await getTemplateAnalytics();
      setTemplateAnalytics(data);
    };
    loadTemplateAnalytics();
  }, [analytics]);

  useEffect(() => {
    // Fetch analytics based on date range
    const startDate = startOfDay(subDays(new Date(), parseInt(dateRange)));
    const endDate = endOfDay(new Date());
    fetchAnalytics(templateId, { start: startDate, end: endDate });
  }, [dateRange, templateId]);

  const stats = getAnalyticsStats(templateId);

  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon: Icon, 
    trend,
    color = "blue" 
  }: {
    title: string;
    value: string | number;
    subtitle: string;
    icon: any;
    trend?: string;
    color?: string;
  }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-gray-500">{subtitle}</p>
            {trend && (
              <Badge variant="outline" className="mt-2 text-xs">
                {trend}
              </Badge>
            )}
          </div>
          <Icon className={`h-8 w-8 text-${color}-500`} />
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Notification Analytics</h2>
          <p className="text-gray-600">
            Track email opens, clicks, and engagement metrics
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Sent"
          value={stats.total_sent.toLocaleString()}
          subtitle="Emails delivered"
          icon={Mail}
          color="blue"
        />
        <StatCard
          title="Open Rate"
          value={`${stats.open_rate.toFixed(1)}%`}
          subtitle={`${stats.total_opened} opens`}
          icon={Eye}
          trend={stats.open_rate > 20 ? "Above average" : "Below average"}
          color="green"
        />
        <StatCard
          title="Click Rate"
          value={`${stats.click_rate.toFixed(1)}%`}
          subtitle={`${stats.total_clicked} clicks`}
          icon={MousePointer}
          trend={stats.click_rate > 3 ? "Above average" : "Below average"}
          color="purple"
        />
        <StatCard
          title="Bounce Rate"
          value={`${stats.bounce_rate.toFixed(1)}%`}
          subtitle={`${stats.total_bounced} bounces`}
          icon={AlertCircle}
          trend={stats.bounce_rate < 2 ? "Healthy" : "Needs attention"}
          color="red"
        />
      </div>

      {/* Detailed Analytics */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="templates">Template Performance</TabsTrigger>
          <TabsTrigger value="recent">Recent Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Engagement Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Engagement Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Email Opens</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${Math.min(stats.open_rate, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{stats.open_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Click Through</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full" 
                          style={{ width: `${Math.min(stats.click_rate * 10, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{stats.click_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Unsubscribe Rate</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ width: `${Math.min(stats.unsubscribe_rate * 20, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{stats.unsubscribe_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top Performing Content */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Performance Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">Best Open Rate</span>
                    </div>
                    <p className="text-xs text-green-600">
                      {stats.open_rate > 25 ? 'Excellent engagement!' : 
                       stats.open_rate > 15 ? 'Good performance' : 
                       'Room for improvement'}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MousePointer className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Click Performance</span>
                    </div>
                    <p className="text-xs text-blue-600">
                      {stats.click_rate > 5 ? 'High engagement content' : 
                       stats.click_rate > 2 ? 'Average performance' : 
                       'Consider improving CTAs'}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">Audience Health</span>
                    </div>
                    <p className="text-xs text-purple-600">
                      {stats.bounce_rate < 2 ? 'Healthy audience' : 
                       stats.bounce_rate < 5 ? 'Monitor bounces' : 
                       'Clean email list needed'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Performance Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              {templateAnalytics.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No template analytics available yet</p>
                  <p className="text-sm text-gray-500">Send some emails to see performance data</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {templateAnalytics.map((template) => (
                    <div key={template.template_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-medium">{template.template_name}</h4>
                          <p className="text-sm text-gray-600">{template.total_sent} emails sent</p>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline">
                            {template.open_rate.toFixed(1)}% opens
                          </Badge>
                          <Badge variant="outline">
                            {template.click_rate.toFixed(1)}% clicks
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="text-center">
                          <p className="text-green-600 font-medium">{template.total_opened}</p>
                          <p className="text-gray-600">Opens</p>
                        </div>
                        <div className="text-center">
                          <p className="text-purple-600 font-medium">{template.total_clicked}</p>
                          <p className="text-gray-600">Clicks</p>
                        </div>
                        <div className="text-center">
                          <p className="text-red-600 font-medium">{template.total_bounced}</p>
                          <p className="text-gray-600">Bounces</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recent" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No recent activity</p>
                  <p className="text-sm text-gray-500">Activity will appear here as emails are sent and opened</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {analytics.slice(0, 10).map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          event.event_type === 'sent' ? 'bg-blue-500' :
                          event.event_type === 'opened' ? 'bg-green-500' :
                          event.event_type === 'clicked' ? 'bg-purple-500' :
                          event.event_type === 'bounced' ? 'bg-red-500' :
                          'bg-gray-500'
                        }`} />
                        <div>
                          <p className="text-sm font-medium">
                            Email {event.event_type}
                          </p>
                          <p className="text-xs text-gray-600">{event.recipient_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {format(new Date(event.created_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificationAnalyticsDashboard;